import React from 'react'
import { SectionPlaceholder } from 'src/components'
import { Stack, SimpleGrid, Text, useMantineTheme, Box } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import Fade from 'react-reveal/Fade'
import { PhilosophyCard } from './components'
import { MenuLinks } from 'src/content'

export interface PhilosophyProps {}

const philosophyPrinciples = [
  { emoji: '🏗️', text: 'UI is a system, not a layer' },
  { emoji: '♻️', text: 'Maintainability beats cleverness' },
  { emoji: '📐', text: 'Scale comes from structure' },
  { emoji: '🤖', text: 'AI is leverage, not hype' },
  { emoji: '📖', text: 'Good code is readable code' },
]

export const Philosophy: React.FC<PhilosophyProps> = () => {
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)
  const { MenuAnchor } = MenuLinks

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '2px',
          background: `linear-gradient(90deg, transparent, ${
            theme.colorScheme === 'dark' ? theme.colors.gray[7] : theme.colors.gray[3]
          }, transparent)`,
          opacity: 0.5,
        }}
      />
      <SectionPlaceholder
        title="How I Think About Engineering"
        subTitle="const { philosophy } = engineering;"
        fluid
        titleColor={theme.colors.gray[2]}
        subtitleColor={theme.colors.cyan[2]}
        customStyles={{
          backgroundColor: '#451599',
          paddingTop: '7rem',
        }}
        anchorClassName={MenuAnchor.Philosophy}
      >
        <Stack align="center" spacing="xl" mt={0}>
          <Fade bottom>
            <Text
              size={isMobile ? 'md' : 'lg'}
              color={theme.colors.gray[2]}
              sx={{ textAlign: 'center', lineHeight: 1.8 }}
              px={isMobile ? 'md' : 'xl'}
            >
              I work at the intersection of UI, architecture, performance, and long-term maintainability.
            </Text>
          </Fade>

          <Box
            sx={{
              width: '100%',
              maxWidth: 900,
              marginTop: theme.spacing.lg,
            }}
          >
            <Fade bottom delay={200}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: theme.radius.xl,
                  padding: theme.spacing.xl,
                  border: `1px solid rgba(255, 255, 255, 0.2)`,
                  boxShadow: `0 4px 20px rgba(0, 0, 0, 0.2)`,
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Text size="xl" weight={700} color={theme.colors.cyan[2]} align="center" mb="lg">
                  My Engineering Philosophy
                </Text>
                <SimpleGrid cols={1} spacing="md">
                  {philosophyPrinciples.map((principle, index) => (
                    <PhilosophyCard key={index} emoji={principle.emoji} text={principle.text} delay={index * 100} />
                  ))}
                </SimpleGrid>
              </Box>
            </Fade>
          </Box>
        </Stack>
      </SectionPlaceholder>
    </>
  )
}
